'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { ReadingSpotlight } from '@/components/motion/ReadingSpotlight';
import { getListingsByIds } from '@/lib/api';
import { paymentBreakdown } from '@/lib/mortgage';
import { priceCutSummary } from '@/lib/derive';
import { availabilityOf, badgeClasses, badgeLabel, isBuyable, isGone } from '@/lib/availability';
import {
  formatPriceFull,
  formatPrice,
  formatBaths,
  getPropertyTypeLabel,
  generateListingSlug,
  titleCaseCity,
  daysSince,
  localTodayISO,
  cn,
} from '@/lib/utils';
import { photoSrc } from '@/lib/photos';
import { cardPhotoLoader } from '@/lib/imageLoader';
import type { Listing } from '@/types/listing';

/**
 * Rows of the comparison table. `value` renders the cell; the optional `num` +
 * `best` let us highlight the winning home per row (lower price, more space, …).
 * `num` returns undefined when the metric doesn't apply (so it's excluded).
 */
interface CompareRow {
  label: string;
  value: (l: Listing) => string;
  num?: (l: Listing) => number | undefined;
  best?: 'min' | 'max';
}

export const ROWS: CompareRow[] = [
  { label: 'Price', value: (l) => formatPriceFull(l.price), num: (l) => l.price, best: 'min' },
  {
    label: 'Price cut',
    value: (l) => {
      const cut = priceCutSummary(l.originalListPrice, l.price);
      return cut ? `−${formatPrice(cut.cut)} (${cut.pct}%)` : '—';
    },
    num: (l) => ((l.originalListPrice ?? 0) > l.price ? l.originalListPrice! - l.price : 0),
    best: 'max',
  },
  {
    // 20% down, 30y, 6.5% — the listing page's defaults; customize there.
    label: 'Est. monthly',
    value: (l) => `${formatPrice(paymentBreakdown(l.price, 20, 6.5, 30, l.hoaFee ?? 0).total)}/mo`,
    num: (l) => paymentBreakdown(l.price, 20, 6.5, 30, l.hoaFee ?? 0).total,
    best: 'min',
  },
  {
    label: '$/sqft',
    value: (l) => ((l.pricePerSqft ?? 0) > 0 ? `$${Math.round(l.pricePerSqft!)}` : '—'),
    num: (l) => ((l.pricePerSqft ?? 0) > 0 ? l.pricePerSqft! : undefined),
    best: 'min',
  },
  // 0 means the county record has no value, not that the home has none. Returning
  // undefined from `num` drops the unknown home out of winnerIndices entirely, so a
  // 1-bath home is no longer crowned as beating a home whose bath count we simply do
  // not know. Same guard shape as Lot and Year built immediately below.
  {
    label: 'Beds',
    value: (l) => ((l.beds ?? 0) > 0 ? String(l.beds) : '—'),
    num: (l) => ((l.beds ?? 0) > 0 ? l.beds : undefined),
    best: 'max',
  },
  {
    label: 'Baths',
    value: (l) => ((l.baths ?? 0) > 0 ? formatBaths(l.baths) : '—'),
    num: (l) => ((l.baths ?? 0) > 0 ? l.baths : undefined),
    best: 'max',
  },
  {
    label: 'Sqft',
    value: (l) => ((l.sqft ?? 0) > 0 ? l.sqft.toLocaleString() : '—'),
    num: (l) => ((l.sqft ?? 0) > 0 ? l.sqft : undefined),
    best: 'max',
  },
  {
    label: 'Lot',
    value: (l) => ((l.lotSizeSqft ?? 0) > 0 ? `${l.lotSizeSqft!.toLocaleString()} sqft` : '—'),
    num: (l) => ((l.lotSizeSqft ?? 0) > 0 ? l.lotSizeSqft! : undefined),
    best: 'max',
  },
  {
    label: 'Year built',
    value: (l) => ((l.yearBuilt ?? 0) > 0 ? String(l.yearBuilt) : '—'),
    num: (l) => ((l.yearBuilt ?? 0) > 0 ? l.yearBuilt! : undefined),
    best: 'max',
  },
  {
    label: 'HOA',
    value: (l) => ((l.hoaFee ?? 0) > 0 ? `$${l.hoaFee!.toLocaleString()}/mo` : 'None'),
    num: (l) => l.hoaFee ?? 0,
    best: 'min',
  },
  {
    // Not "days on market": the feed never populates DaysOnMarket, so this is
    // days since `list_date` and a relist doesn't reset it. The label carries
    // the unit because a comparison column reads best as a bare number.
    label: 'Days since listed',
    // The table only renders once the query has resolved, which never happens
    // during SSR — so reading "today" here is hydration-safe. (It used to be
    // safe because the page was auth-gated; that gate is gone as of R126, the
    // reason isn't.) Mirrors the listing page's derivation.
    value: (l) => String(l.daysOnMarket || daysSince(l.listDate, localTodayISO()) || '—'),
  },
  { label: 'Type', value: (l) => getPropertyTypeLabel(l.propertyType) },
];

/**
 * Indices of the winning home(s) for a row. Empty when the metric doesn't
 * apply, fewer than two homes have it, or every home ties (no meaningful win).
 *
 * **Only homes that can still be bought take part.** A comparison exists to help
 * a buyer choose, and declaring an expired home the winner on price — in emerald,
 * as "the best value" — recommends a home nobody can buy. If fewer than two homes
 * are still available there is nothing left to choose between, so no row has a
 * winner at all. (W16 R154)
 */
export function winnerIndices(row: CompareRow, listings: Listing[]): Set<number> {
  if (!row.num || !row.best) return new Set();
  const entries = listings
    .map((l, i) => ({ n: row.num!(l), i, buyable: isBuyable(availabilityOf(l.status)) }))
    .filter((e) => e.buyable)
    .filter((e): e is { n: number; i: number; buyable: boolean } =>
      typeof e.n === 'number' && Number.isFinite(e.n)
    );
  if (entries.length < 2) return new Set();
  const values = entries.map((e) => e.n);
  if (new Set(values).size === 1) return new Set(); // all tie → no winner
  const target = row.best === 'min' ? Math.min(...values) : Math.max(...values);
  return new Set(entries.filter((e) => e.n === target).map((e) => e.i));
}

export default function CompareClient() {
  const router = useRouter();
  const params = useSearchParams();
  // Two routes render this: `/compare` (what the app hands out) and the original
  // `/favorites/compare` (still live for existing links). Self-navigation has to
  // stay on whichever one the buyer is actually on. (W13 R127)
  const pathname = usePathname();
  const { user, isInitialized } = useAuthStore();

  // Deduped, because `?ids=a,a` is a link people really build by clicking
  // Compare twice — and because `ids.length` is now load-bearing: it is what the
  // page compares against the catalog's answer to know a home is missing.
  const ids = useMemo(
    () => [...new Set((params.get('ids') ?? '').split(',').filter(Boolean))].slice(0, 4),
    [params]
  );

  /** Drop a home from the comparison; below two there's nothing left to compare. */
  const removeListing = (id: string) => {
    const next = ids.filter((x) => x !== id);
    // A guest has no favorites page to be sent to.
    if (next.length < 2) router.push(user ? '/favorites' : '/homes');
    else router.replace(`${pathname}?ids=${next.join(',')}`);
  };

  /**
   * No sign-in wall. Every row of this table is public listing data — price,
   * price cut, est. monthly, $/sqft, beds/baths/sqft — all of it already on the
   * public detail page. The wall was incidental: the route lives under
   * `/favorites/`, so it inherited the favorites auth check.
   *
   * Verified on the deployed site: a shared comparison link bounced a guest to
   * **`/login?redirect=/favorites`** — so the `?ids=` were thrown away, and even
   * after signing in the recipient landed on their own favorites rather than the
   * comparison they were sent. A comparison link was unshareable twice over.
   * (W13 R126)
   */
  /**
   * `isError` is the state this page could not previously reach, and it is the
   * one the copy below is most wrong about. `getListingsByIds` used to swallow
   * every failure into an omission, so a 500 from `/listings/{id}` arrived here
   * as a short list and the page announced *"These homes aren't listed any more,
   * so there's nothing left to compare"* — an epitaph for two homes that are on
   * the market, from a shared link that works fine. It now rejects for anything
   * that is not a 404, which is what makes the branch below reachable.
   */
  const { data: listings, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['compare', ids.join(',')],
    queryFn: () => getListingsByIds(ids),
    enabled: ids.length >= 2,
  });

  /**
   * What the table is actually working with. `ids` is what the link asked for;
   * `listings` is what the catalog still holds; `buyable` is what a buyer could
   * still choose. All three can differ, and the page has to say so rather than
   * quietly rendering the intersection.
   */
  const resolved = listings ?? [];
  const missingCount = Math.max(0, ids.length - resolved.length);
  const buyableCount = resolved.filter((l) => isBuyable(availabilityOf(l.status))).length;
  const unavailableCount = resolved.length - buyableCount;

  // Signed-in buyers came from their favorites; a guest has no favorites to
  // return to, so send them somewhere that exists.
  const backHref = user ? '/favorites' : '/homes';
  const backLabel = user ? 'Back to favorites' : 'Back to search';

  if (ids.length < 2) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="font-display text-3xl text-ink">Pick homes to compare</h1>
        <p className="mt-3 text-ink-muted">
          {/* Don't send a guest to a page they can't open. */}
          {isInitialized && !user ? (
            <>
              Open any two homes and use <span className="font-medium text-ink">Compare</span> on a
              similar home, or{' '}
              <Link href="/login" className="font-medium text-emerald-300 hover:text-emerald-700">
                log in
              </Link>{' '}
              to compare your saved homes.
            </>
          ) : (
            <>
              Select two to four saved homes on your{' '}
              <Link
                href="/favorites"
                className="font-medium text-emerald-300 hover:text-emerald-700"
              >
                favorites page
              </Link>{' '}
              and hit Compare.
            </>
          )}
        </p>
        <Link
          href="/homes"
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-300 hover:text-emerald-700"
        >
          Browse homes
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Link
        href={backHref}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        {backLabel}
      </Link>
      <h1 className="font-display text-3xl tracking-tight text-ink sm:text-4xl">Compare homes</h1>
      {/* The emerald promise is only made when there are at least two homes left
          that a buyer could actually choose between. Highlighting the "best value"
          across a set where the winner has left the market recommends a home
          nobody can buy. (W16 R154) */}
      {buyableCount >= 2 && (
        <p className="mt-2 text-sm text-ink-muted">
          The <span className="font-medium text-emerald-300">emerald</span> value wins each row — lowest
          price, most space, and so on.
        </p>
      )}
      {/* Homes in the link the catalog no longer holds at all. `getListingsByIds`
          omits them silently, so a three-home comparison used to render as a
          two-home one with nothing to say a home was missing. (W16 R154) */}
      {missingCount > 0 && (listings?.length ?? 0) >= 2 && (
        <p className="mt-2 text-sm text-ink-muted">
          {missingCount === 1
            ? 'One home in this link is no longer in the MLS catalog, so it isn’t in the table.'
            : `${missingCount} homes in this link are no longer in the MLS catalog, so they aren’t in the table.`}
        </p>
      )}
      {unavailableCount > 0 && buyableCount >= 1 && (
        <p className="mt-2 text-sm text-ink-muted">
          {unavailableCount === 1
            ? 'One of these homes has left the market — it’s kept here for reference and excluded from the row-by-row comparison.'
            : `${unavailableCount} of these homes have left the market — they’re kept here for reference and excluded from the row-by-row comparison.`}
        </p>
      )}

      {isError ? (
        /* We could not ask. Nothing here is news about the homes, so nothing
           here may sound like it. */
        <div className="mt-10 max-w-lg">
          <p className="text-ink">We couldn&rsquo;t load these homes just now.</p>
          <p className="mt-2 text-sm text-ink-muted">
            This is us, not the listings — the link is fine and the homes may well still be on the
            market. Try again in a moment.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="text-sm font-medium text-emerald-300 hover:text-emerald-700 disabled:opacity-60"
            >
              {isFetching ? 'Loading…' : 'Try again'}
            </button>
            <Link
              href="/homes"
              className="text-sm font-medium text-emerald-300 hover:text-emerald-700"
            >
              Browse homes
            </Link>
          </div>
        </div>
      ) : isLoading || !listings ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
        </div>
      ) : listings.length < 2 ? (
        /* The homes in the link no longer resolve. `getListingsByIds` silently
           drops ids it can't fetch, so `listings` comes back short and the table
           rendered its twelve row labels with **zero columns** — a full skeleton
           of "Price / Price cut / Est. monthly / …" against nothing, under copy
           promising that "the emerald value wins each row". Seen live on
           `/compare?ids=not-a-uuid,also-not`, which is also what a shared link
           looks like once its homes sell and are purged from the index — and 5,120
           sold/deleted listings were purged this week, so those links exist.
           (W13 R130) */
        <div className="mt-10 max-w-lg">
          <p className="text-ink">
            {listings.length === 1
              ? 'Only one of these homes is still listed, so there’s nothing to compare it with.'
              : 'These homes aren’t listed any more, so there’s nothing left to compare.'}
          </p>
          <p className="mt-2 text-sm text-ink-muted">
            Homes leave the MLS when they sell or expire. A link to a comparison stops working when they
            do.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            {listings.length === 1 && (
              <Link
                href={generateListingSlug(listings[0])}
                className="text-sm font-medium text-emerald-300 hover:text-emerald-700"
              >
                See {listings[0].address}
              </Link>
            )}
            <Link
              href="/homes"
              className="text-sm font-medium text-emerald-300 hover:text-emerald-700"
            >
              Browse homes
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          {/* Twelve rows of money across up to four columns, read by tracking
              one row sideways — the exact place people lose their line. The
              spotlight is an aid over that and nothing else: it draws behind
              the cells, is `aria-hidden`, and never fires for a keyboard, a
              touchscreen or reduced motion. Every cell below keeps its own ink
              or emerald token, so the table reads identically with the glow
              absent, which is how most people will see it.

              `inline-block min-w-full` keeps the host sized to the table rather
              than to the scroll viewport, so the glow covers the full width and
              `getBoundingClientRect` stays honest under horizontal scroll —
              the host must never be the scrolling element itself. */}
          <ReadingSpotlight className="inline-block min-w-full align-top">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="w-36" aria-label="Attribute" />
                  {listings.map((l) => (
                    <th key={l.id} className="px-3 pb-4 text-left align-top font-normal">
                      <div className="group relative">
                        {/* Drop this home from the comparison (keeps ≥2) */}
                        {listings.length > 2 && (
                          <button
                            onClick={() => removeListing(l.id)}
                            aria-label={`Remove ${l.address} from comparison`}
                            className="glass-blur absolute right-1 top-1 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/70"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                        <Link href={generateListingSlug(l)} className="block">
                          <span className="relative block h-28 w-full overflow-hidden rounded-xl">
                            <Image
                              src={photoSrc(l.photos?.[0], 'card')}
                              loader={cardPhotoLoader}
                              alt={l.address}
                              fill
                              className="object-cover transition-transform group-hover:scale-105"
                              sizes="240px"
                            />
                            {/* Same dim the card uses for a home that has gone, so
                                the two surfaces read alike. */}
                            {isGone(availabilityOf(l.status)) && (
                              <span aria-hidden className="absolute inset-0 bg-canvas/45" />
                            )}
                          </span>
                          <span className="mt-2 block font-medium text-ink group-hover:text-emerald-300">
                            {l.address}
                          </span>
                          {/* Availability on the column itself: a table of numbers
                              reads as a set of equally live options otherwise. */}
                          {badgeLabel(availabilityOf(l.status)) && (
                            <span
                              className={cn(
                                'mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold',
                                badgeClasses(availabilityOf(l.status))
                              )}
                            >
                              {badgeLabel(availabilityOf(l.status))}
                            </span>
                          )}
                          <span className="block text-ink-muted">
                            {titleCaseCity(l.city)}, {l.zip}
                          </span>
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => {
                  const winners = winnerIndices(row, listings);
                  return (
                    <tr key={row.label} className="border-t border-hairline">
                      <th scope="row" className="py-3 pr-3 text-left font-medium text-ink-muted">
                        {row.label}
                      </th>
                      {listings.map((l, i) => (
                        <td
                          key={l.id}
                          className={cn(
                            'px-3 py-3 tabular-nums',
                            winners.has(i)
                              ? 'font-semibold text-emerald-300'
                              : /* An off-market column is muted, not just
                                   un-highlighted. Read in the deployed screenshot,
                                   an emerald "$1,091/sqft" next to a plain "$515"
                                   looks like the table picked the wrong winner; the
                                   numbers have to visibly sit out of the comparison
                                   the notice above says they are out of. (W16 R158) */
                                isBuyable(availabilityOf(l.status))
                                ? 'text-ink-soft'
                                : 'text-ink-faint'
                          )}
                        >
                          {row.value(l)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ReadingSpotlight>
        </div>
      )}
    </div>
  );
}
